import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { FeatureFlagNames } from 'libs/end-to-end/src/feature-flag';
import { FeatureFlags } from 'libs/end-to-end/src/feature-flag';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';

export const AdminFeatureFlagsPage = () => {
  const { featureFlags, userAuthenticatedApiCall, loadFeatureFlags } =
    useUser();
  const navigate = useNavigateSafe();

  const [isValid, setIsValid] = useState(true);
  const [mostRecentErrorMessage, setMostRecentErrorMessage] = useState('');
  const [editableFeatureFlags, setEditableFeatureFlags] =
    useState<FeatureFlags | null>(null);

  const readyToDisplayPage = useMemo(() => {
    return featureFlags !== null && editableFeatureFlags !== null;
  }, [featureFlags, editableFeatureFlags]);

  useEffect(() => {
    if (featureFlags !== null) {
      setEditableFeatureFlags(featureFlags);
    }
  }, [featureFlags]);

  const rows = useMemo(() => {
    if (!readyToDisplayPage || !editableFeatureFlags) {
      return [];
    }

    const actualRows: AdminTableRow[] = [];

    FeatureFlagNames.map((featureFlag) => {
      actualRows.push({
        key: featureFlag,
        columns: [
          {
            columnType: 'text',
            key: 'name',
            text: featureFlag,
          },
          {
            columnType: 'boolean',
            key: 'enabled',
            value: editableFeatureFlags[featureFlag] ?? false,
            onChange: (newValue) => {
              setEditableFeatureFlags((prev) => ({
                ...prev,
                [featureFlag]: newValue,
              }));
            },
          },
        ],
      });
    });

    return actualRows;
  }, [editableFeatureFlags, readyToDisplayPage]);

  const handleSave = async () => {
    if (!isValid || !readyToDisplayPage) {
      return;
    }

    setIsValid(false);

    const featureFlagsResult = await userAuthenticatedApiCall({
      responseSchema: FeatureFlags,
      url: `${getApiBaseUrl()}/feature-flags`,
      requestOptions: {
        method: 'PUT',
        body: JSON.stringify(editableFeatureFlags),
      },
    });

    if (
      featureFlagsResult === null ||
      featureFlagsResult.resultType === 'err'
    ) {
      setMostRecentErrorMessage(
        'Something went wrong when saving feature flags. Please try again.',
      );
      setIsValid(true);
      return;
    }

    loadFeatureFlags();

    setIsValid(true);
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  return (
    <AdminLayout currentPage={'AdminFeatureFlags'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Feature Flags', to: '/feature-flags' },
        ]}
      />
      <p>You can manage feature flags from this screen.</p>

      {!readyToDisplayPage && (
        <Spacing direction="horizontal" size="sm">
          <Spinner size={24} />
        </Spacing>
      )}

      {readyToDisplayPage && (
        <>
          {mostRecentErrorMessage && (
            <div className="pt-3 text-red-500">
              <p>{mostRecentErrorMessage}</p>
            </div>
          )}

          <AdminTable headings={['Name', 'Enabled']} rows={rows} />

          <div className="flex flex-row w-full pt-3">
            <div className="flex-grow" />

            <LegacyButton
              type="button"
              onClick={handleSave}
              variant={LegacyButtonVariant.PRIMARY_LIGHT}
              disabled={!isValid}
            >
              Save
            </LegacyButton>

            <LegacyButton
              type="button"
              onClick={handleCancel}
              variant={LegacyButtonVariant.PRIMARY_LIGHT}
            >
              Cancel
            </LegacyButton>
          </div>
        </>
      )}
    </AdminLayout>
  );
};
